import { useState } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { DOCUMENT_TYPES, type DocumentType } from "../types";

const FIELD_CLASS =
  "h-11 w-full rounded-md border border-input bg-background px-3 text-sm sm:h-9";

/** Runs a dialog action and returns its error message, or null on success. */
export type DialogSubmit<Value> = (value: Value) => Promise<string | null>;

/**
 * Reason prompt for Observar / Rechazar / Eliminar: the reason is required
 * before Confirmar enables, and the server's Spanish error is shown inline.
 */
export const ReasonDialog = ({
  title,
  description,
  onClose,
  onSubmit,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  onSubmit: DialogSubmit<string>;
}) => {
  const translate = useTranslate();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    const message = await onSubmit(reason.trim());
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <label className="space-y-1 text-sm">
          <span>{translate("crm.operations.documents.reason")}</span>
          <Textarea
            value={reason}
            required
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        {error ? (
          <p role="alert" className="text-sm text-[#B3321C]">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {translate("crm.operations.documents.cancel")}
          </Button>
          <Button
            type="button"
            className="h-11 sm:h-9"
            disabled={busy || reason.trim() === ""}
            onClick={confirm}
          >
            {translate("crm.operations.documents.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export type NewDocumentValue = {
  file: File;
  docType: DocumentType;
  title: string;
};

/** "Subir documento": type, optional title and the file; the provider hashes it. */
export const UploadDialog = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: DialogSubmit<NewDocumentValue>;
}) => {
  const translate = useTranslate();
  const [docType, setDocType] = useState<DocumentType>(DOCUMENT_TYPES[0]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    const message = await onSubmit({ file, docType, title: title.trim() });
    setBusy(false);
    if (message) setError(message);
  };

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.operations.documents.upload_title")}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.operations.documents.upload_hint")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block space-y-1">
            <span>{translate("crm.operations.documents.doc_type")}</span>
            <select
              className={FIELD_CLASS}
              value={docType}
              onChange={(event) =>
                setDocType(event.target.value as DocumentType)
              }
            >
              {DOCUMENT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {translate(`crm.operations.documents.types.${type}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span>{translate("crm.operations.documents.title_optional")}</span>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <label className="block space-y-1">
            <span>{translate("crm.operations.documents.file")}</span>
            <input
              type="file"
              className={`${FIELD_CLASS} py-2 sm:py-1`}
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-[#B3321C]">
            {error}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={onClose}
          >
            {translate("crm.operations.documents.cancel")}
          </Button>
          <Button
            type="button"
            className="h-11 sm:h-9"
            disabled={busy || !file}
            onClick={submit}
          >
            {translate("crm.operations.documents.submit_upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
